/**
 * byo-v4-quicklook.js — Quick Look (P3b).
 *
 * Ported by name from `byo-quickview-v2.jsx` so a reviewer can diff component
 * for component: `QtyStepper`, `DETAIL_SECTIONS`, `DetailBody`, `Details`,
 * `QuickView`. DESIGN-SPEC.md §3.8.
 *
 * One structural departure from the JSX, and it is the house style rather than
 * a design change: the design branches on a `wide` prop and returns two whole
 * dialogs. This renders one DOM and lets `byo-v4-builder.css` switch between
 * the phone bottom sheet and the 880px desktop dialog, because every other
 * component in this build is CSS-responsive and a second DOM would have to be
 * kept in step by hand.
 *
 * Metafield shapes are guarded rather than trusted. OPEN-QUESTIONS.md #18 says
 * the quick-look metafield keys were never verified against the live store, so
 * every accordion body checks the shape it was handed and falls back to
 * printing the value rather than throwing inside a dialog the customer opened.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, useState, useEffect, useRef } from './byo-v4-vendor.js';
const { Icon, Btn, Pill, ProductImage, SizeChip, MeterText, focusInto, focusBack } = (window.BYOV4 = window.BYOV4 || {});
/* Layer comes from byo-v4-ui.js (D60). If a cached, older ui.js is on the page
   it is missing, and the sheet is drawn in place rather than not at all. */
const Layer = (window.BYOV4 && window.BYOV4.Layer) || (({ children }) => children);
/**
 * QtyStepper — byo-quickview-v2.jsx:2. The plum pill stepper inside the sheet
 * footer. 44px here rather than the card's 40px: the design sizes it up for
 * the dialog, and it is also the accessibility floor.
 */
export function QtyStepper({ p, b }) {
  const qty = b.qtyOf(p.id);
  const ok = b.fits(p) || b.itemsMode;

  return html`
    <div class="byo-v4-qty">
      <button class="byo-v4-qty__btn" onClick=${() => b.remove(p.id)} aria-label="Remove one">
        <${Icon} name="minus" size=${18} stroke=${2.5} />
      </button>
      <span class="byo-v4-qty__n">${qty}</span>
      <button
        class="byo-v4-qty__btn"
        onClick=${(e) => b.add(p.id, e.currentTarget)}
        aria-label="Add one more"
        disabled=${!ok}
      >
        <${Icon} name="plus" size=${18} stroke=${2.5} />
      </button>
    </div>
  `;
}

/**
 * Which detail sections the sheet can show, in order, and the metafield each
 * reads. A section renders only when the product carries that field.
 * byo-quickview-v2.jsx:12 — same keys, same order, same labels.
 */
export const DETAIL_SECTIONS = [
  { key: 'ingredients', label: 'Ingredients' },
  { key: 'allergens', label: 'Allergens' },
  { key: 'nutrition', label: 'Nutrition' },
  { key: 'notes', label: 'Fragrance notes' },
  { key: 'usage', label: 'How to use' },
  { key: 'pairing', label: 'Pairs well with' },
  { key: 'brand', label: 'About the maker' },
];

/** A value worth rendering: not null, not an empty string, not an empty list. */
function present(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

/** Last-resort rendering for a metafield that is not the shape #18 guessed. */
function asText(v) {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map((x) => (Array.isArray(x) ? x.join(': ') : String(x))).join(' · ');
  return JSON.stringify(v);
}

/**
 * DetailBody — byo-quickview-v2.jsx:21. Four renderings: the nutrition table,
 * the allergen chips, the fragrance-note pairs, and a paragraph for the rest.
 */
export function DetailBody({ s, d, vendor }) {
  const v = d[s.key];

  if (s.key === 'nutrition') {
    const rows = v && Array.isArray(v.rows) ? v.rows : null;
    if (!rows) return html`<p class="byo-v4-ql__text">${asText(v)}</p>`;
    return html`
      <div>
        <div class="byo-v4-ql__per">Typical values per ${v.per || '100g'}</div>
        <div class="byo-v4-ql__nutrition">
          ${rows.map(([k, val]) => {
            const sub = String(k).startsWith('of which');
            return [
              html`<span class=${`byo-v4-ql__nk${sub ? ' is-sub' : ''}`}>${k}</span>`,
              html`<span class="byo-v4-ql__nv">${val}</span>`,
            ];
          })}
        </div>
      </div>
    `;
  }

  if (s.key === 'allergens') {
    const contains = v && Array.isArray(v.contains) ? v.contains : null;
    const note = v && typeof v.note === 'string' ? v.note : null;
    if (!contains && !note) return html`<p class="byo-v4-ql__text">${asText(v)}</p>`;
    return html`
      <div>
        ${contains && contains.length
          ? html`<div class="byo-v4-ql__chips">
              ${contains.map((a) => html`<${Pill} key=${a} tone="orange">${a}<//>`)}
            </div>`
          : html`<p class="byo-v4-ql__text">No declared allergens.</p>`}
        ${note && html`<p class="byo-v4-ql__text">${note}</p>`}
      </div>
    `;
  }

  if (s.key === 'notes') {
    const pairs = Array.isArray(v) && v.every((x) => Array.isArray(x)) ? v : null;
    if (!pairs) return html`<p class="byo-v4-ql__text">${asText(v)}</p>`;
    return html`
      <div class="byo-v4-ql__notes">
        ${pairs.map(([k, val]) => [
          html`<span class="byo-v4-ql__nokey">${k}</span>`,
          html`<span>${val}</span>`,
        ])}
      </div>
    `;
  }

  return html`<p class="byo-v4-ql__text">${asText(v)}</p>`;
}

/**
 * Details — byo-quickview-v2.jsx:31. The accordion stack. The first section is
 * open on open, and the open one resets whenever the product changes.
 */
export function Details({ d, vendor }) {
  const secs = DETAIL_SECTIONS.filter((s) => d && present(d[s.key]));

  /* All collapsed on open — Alan's call, DEVIATIONS.md D32. The design opens
     the first section (`React.useState(0)`, byo-quickview-v2.jsx:33), which put
     a box of metafield text directly under the description and made the two
     compete. The description is the thing that should be read first, and it
     sits outside this component as plain text. */
  const [on, setOn] = useState(-1);

  useEffect(() => {
    setOn(-1);
  }, [d]);

  if (!secs.length) return null;

  return html`
    <div class="byo-v4-ql__details" data-screen-label="Product details">
      ${secs.map((s, i) => {
        const isOn = on === i;
        return html`
          <div class=${`byo-v4-ql__acc${i === secs.length - 1 ? ' is-last' : ''}`} key=${s.key}>
            <button class="byo-v4-ql__accbtn" onClick=${() => setOn(isOn ? -1 : i)} aria-expanded=${isOn}>
              <span class="byo-v4-ql__acclabel">${s.key === 'brand' ? `About ${vendor}` : s.label}</span>
              <span class=${`byo-v4-ql__accmark${isOn ? ' is-open' : ''}`} aria-hidden="true">
                <${Icon} name="plus" size=${13} stroke=${2.5} />
              </span>
            </button>
            ${isOn && html`<div class="byo-v4-ql__accbody"><${DetailBody} s=${s} d=${d} vendor=${vendor} /></div>`}
          </div>
        `;
      })}
    </div>
  `;
}

/**
 * QuickView — byo-quickview-v2.jsx:44.
 *
 * `b.peek` holds the product id or null. The last opened product is kept in a
 * ref so the sheet can animate out with its content still drawn, exactly as
 * the design does.
 *
 * The dietary chips depart from the design and the reason is the data, not
 * taste: the design draws "Contains Alcohol" from a negative marker, while the
 * live metafield carries a positive `Alcohol free` literal (the same inversion
 * OPEN-QUESTIONS.md #12 records for the filter pills). Inverting it would
 * assert that every untagged mug contains alcohol. The chips therefore show
 * what the product actually carries. DEVIATIONS.md D27.
 */
export function QuickView({ b }) {
  const p = b.peek != null ? b.byId[b.peek] : null;
  const open = !!p;

  const last = useRef(null);
  if (p) last.current = p;
  const q = last.current;

  const [idx, setIdx] = useState(0);
  const dialog = useRef(null);
  const restoreTo = useRef(null);

  useEffect(() => {
    setIdx(0);
  }, [b.peek]);

  /* Escape closes, and focus is trapped while open — BUILD-PLAN P3 exit. */
  useEffect(() => {
    if (!open) return undefined;

    restoreTo.current = document.activeElement;

    const focusables = () =>
      Array.from(
        dialog.current
          ? dialog.current.querySelectorAll(
              'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          : []
      ).filter((el) => el.offsetParent !== null);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        b.closePeek();
        return;
      }
      if (e.key !== 'Tab') return;

      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const lastEl = list[list.length - 1];

      /* A click inside the dialog can leave focus on the dialog itself or on a
         non-focusable node; without this the first Tab escapes to the page
         behind. This is the same trap the P1 audit's F1 fix put on the reviews
         pop-out. */
      if (!dialog.current || !dialog.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      /* Opened by a tap, the dialog holds focus itself (D132). */
      if (document.activeElement === dialog.current) {
        e.preventDefault();
        (e.shiftKey ? lastEl : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const list = focusables();
      focusInto(dialog.current, list[0]);
    }, 60);

    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
      focusBack(restoreTo.current);
    };
  }, [open, b]);

  if (!q) return null;

  const qty = b.qtyOf(q.id);
  const ok = b.fits(q);
  const why = b.reason(q);
  const imgs = q.images && q.images.length ? q.images : [null];
  const d = q.details;
  const specs = d && Array.isArray(d.specs) ? d.specs : null;
  const items = b.itemsMode;
  const live = ok || items;
  const diet = q.dietaries || [];

  const close = html`
    <button class="byo-v4-ql__close" onClick=${b.closePeek} aria-label="Close">
      <${Icon} name="close" size=${18} />
    </button>
  `;

  /* D70: counts as words, and a refusal says the box is too full rather than
     that the product will not fit. */
  const footLine = qty > 0
    ? `${window.BYO && window.BYO.Words ? window.BYO.Words(qty) : qty} in your box`
    : items
      ? ok
        ? 'Fits in your box'
        : b.atMost && b.atMost(q.size)
          ? `Only ${window.BYO.words(b.most[q.size])} ${q.size.toLowerCase()} per box`
          : 'Your box is too full for this'
      : ok
        ? `Takes ${b.slotWord(q.size)}`
        : why;

  return html`<${Layer}>
    <div class=${`byo-v4-ql${open ? ' is-open' : ''}`}>
      <div class="byo-v4-ql__backdrop" onClick=${b.closePeek}></div>

      <div class="byo-v4-ql__dialog" role="dialog" aria-modal="true" tabindex="-1" aria-label=${q.title} data-byo-peek ref=${dialog}>
        <div class="byo-v4-ql__head">
          <div class="byo-v4-ql__grab" aria-hidden="true"></div>
          <div class="byo-v4-ql__headrow">
            <span class="byo-v4-ql__eyebrow">Quick Look</span>
            ${close}
          </div>
        </div>

        <div class="byo-v4-ql__body">
          <div class="byo-v4-ql__gallery">
            <div class="byo-v4-ql__main">
              ${imgs[idx]
                ? html`<img class="byo-v4-ql__img" src=${imgs[idx]} alt="" />`
                : html`<${ProductImage} p=${q} fit="contain" />`}
              ${imgs.length > 1 &&
              html`<div class="byo-v4-ql__count"><${Pill} tone="white">${idx + 1} / ${imgs.length}<//></div>`}
            </div>
            ${imgs.length > 1 &&
            html`<div class="byo-v4-ql__thumbs">
              ${imgs.map(
                (src, i) => html`
                  <button
                    key=${i}
                    class=${`byo-v4-ql__thumb${i === idx ? ' is-on' : ''}`}
                    onClick=${() => setIdx(i)}
                    aria-label=${`Image ${i + 1}`}
                    aria-current=${i === idx}
                  >
                    <img src=${src} alt="" />
                  </button>
                `
              )}
            </div>`}
          </div>

          <div class="byo-v4-ql__info">
            ${/* Desktop only. The phone sheet's close lives in the header bar
                 beside the "Quick Look" label; CSS shows exactly one of them,
                 and the focus trap counts only the visible one. */ ''}
            <div class="byo-v4-ql__closerow">${close}</div>
            <div class="byo-v4-ql__vendor">${q.vendor}</div>
            <h2 class="byo-v4-ql__title">${q.title}</h2>
            <div class="byo-v4-ql__price">${b.money(q.price)}</div>

            ${/* D70: the same tags as the card's picture. The size is the lime
                 one, Vegan and AF the white outlined ones, so the product
                 reads the same wherever it is looked at. */ ''}
            <div class="byo-v4-ql__chips">
              <${SizeChip} p=${q} itemsMode=${items} slotWord=${b.slotWord} tone="lime" />
              ${diet.includes('Vegan') && html`<${Pill} tone="white" class="byo-v4-dtag">Vegan<//>`}
              ${diet.includes('Alcohol free') &&
              html`<${Pill} tone="white" class="byo-v4-dtag"
                ><span aria-hidden="true">AF</span><span class="byo-v4-sr">Alcohol-free</span><//
              >`}
            </div>

            ${specs &&
            html`<div class="byo-v4-ql__specs" data-screen-label="Product specs">
              ${specs.map(
                ([k, v]) => html`<span key=${k}><span class="byo-v4-ql__speck">${k} </span><strong>${v}</strong></span>`
              )}
            </div>`}

            ${q.desc
              ? html`<p class="byo-v4-ql__desc">${q.desc}</p>`
              : html`<p class="byo-v4-ql__desc byo-v4-ql__desc--empty">
                  Description, gallery and details pull from this product’s Shopify page and metafields.
                </p>`}

            ${/* "Why we love it" (Alan, 30 Sep 2026; D105): the ready-made page
                 v2's component, from custom.product_info, under the
                 description as that design places it. */ ''}
            ${d &&
            d.why &&
            html`<div class="byo-v4-ql__why">
              <b>Why we love it</b>
              <p>${d.why}</p>
            </div>`}

            <${Details} d=${d} vendor=${q.vendor} />

            <div class="byo-v4-ql__fineprint">Not sold separately. It travels inside your Wrapt box.</div>
          </div>
        </div>

        <div class="byo-v4-ql__footer">
          <div class="byo-v4-ql__footmeter">
            <${MeterText} b=${b} />
            <div class="byo-v4-ql__footline">${footLine}</div>
          </div>
          ${qty > 0
            ? html`<${QtyStepper} p=${q} b=${b} />`
            : html`<${Btn}
                onClick=${(e) => {
                  const fits = b.fits(q);
                  b.add(q.id, e.currentTarget);
                  /* Closes on the first successful add so the fly-in is
                     visible — byo-quickview-v2.jsx:1, audit #10. */
                  if (fits) b.closePeek();
                }}
                disabled=${!live}
                icon="plus"
                >Add To Box<//
              >`}
        </div>
      </div>
    </div>
  <//>`;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { DETAIL_SECTIONS, DetailBody, Details, QtyStepper, QuickView });
